//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardHeader,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  InsertComment,
  Cancel, CheckCircle, Gavel, CircleOutlined,
  ExpandLess, ExpandMore,
} from '@mui/icons-material';

// === API ===
import { onCorrectionMode, overruleFileException, removeListener, requestCorrectionMode, updateFileRuleComment } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===
import { Field } from '../../components/Field/Field';
import { CommentModal } from '../../components/CommentModal/CommentModal';
import { Spacer } from 'components/Spacer';

// === HOOKS ===
import { useAnalytics } from 'pages/DocAnalytics/AnalyticsContext/AnalyticsContext';

//#endregion

//-----------------------------------------------------------------
export function Exception( { data } ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const ref = useRef();

  const theme = useTheme();

  const { file_id, review_flags, document_current } = useAnalytics();

  const [ is_open  , setIsOpen  ] = useState( false );
  const [ is_open2 , setIsOpen2 ] = useState( false );
  const [ show     , setShow    ] = useState( false );
  const [ status   , setStatus  ] = useState( data.status  );
  const [ comment  , setComment ] = useState( data.comment );

  const _status_icons = {
    0 : <Cancel color='error'/>,
    1 : <CheckCircle color='success'/>,
    2 : <Gavel color='success'/>,
    3 : <CircleOutlined color='success'/>,
    4 : <Gavel color='error'/>,
  }

  const expand_icon = show ? <ExpandLess fontSize='large' /> : <ExpandMore fontSize='large' />;

  let tooltip_overrule = '';
  switch( status ) {
    case 0 : tooltip_overrule = 'Change the status of this rule to Passed (Overruled).'; break;
    case 1 : tooltip_overrule = 'Change the status of this rule to Failed (Overruled).'; break;
    case 2 : tooltip_overrule = 'Revert the status of this rule to Failed.'; break;
    case 4 : tooltip_overrule = 'Revert the status of this rule to Passed.'; break;
  }

  //#endregion

  //#region [STYLES]

  const _styles = {
    field_paper: {
      gridColumn: 'span 4',
      overflow : 'auto'     ,
      position : 'relative' ,
      width    : '100%',
      boxShadow: 0,
      overflow: 'hidden',
    },
    document_container: {
      width: '100%',
      overflow: 'hidden',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],

      scrollbarWidth: "none", // Hide the scrollbar for firefox
      '&::-webkit-scrollbar': {
          display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
      },
      '&-ms-overflow-style:': {
          display: 'none', // Hide the scrollbar for IE
      },
    },
    card: {
      variant: 'primary',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    header: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    footer: {
      display: 'block',
      height: '4rem',
      padding: '0.5rem',
      paddingLeft: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    overrule: {
      display: '',
    },
    comment: {
      display: '',
    },
  }

  const
    CONTAINER = {
      ref: ref,
      sx : _styles.field_paper,
    },
    CARD = {
      variant: 'primary',
      sx: {
        overflow: 'hidden',
        backgroundColor: theme.palette.background.primary[theme.palette.mode],
        marginRight: '3px',
      }
    },
    HEADER = {
      sx: _styles.header,
      title: data.id + '. ' + data.description,
      children: data.id + '. ' + data.description
    },
    FOOTER = {
      sx: _styles.footer,
    },
    BUTTON_OVERRULE = {
      sx       : _styles.overrule,
      variant  : 'contained',
      color    : 'primary',
      onClick: () => { setIsOpen2(true) },
      children : [2,4].includes( status ) ? 'REVERT' : 'OVERRULE',
      name     : 'da_overrule_exceptions_btn',
    },
    COMMENT = {
      sx       : _styles.comment,
      onClick: () => { setIsOpen(true) },
      name     :'da_comment_exceptopns_btn'
    };

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    setStatus(data.status);
    setComment(data.comment);
  }, [data.status, data.comment]);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onModalClose( event ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen (false)
      setIsOpen2(false)
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onOverrideModalConfirm( text ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen2(false)
      overruleFileException( data.id, text );
      switch( status ) {
        case 0 : setStatus( 2 ); break;
        case 1 : setStatus( 4 ); break;
        case 2 : setStatus( 0 ); break;
        case 4 : setStatus( 1 ); break;
      }
      setComment( text );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onCommentModalConfirm( text ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen(false)
      updateFileRuleComment( data.id, text );
      setComment( text );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_Open( event ) {
    //-----------------------------------------------------------------
    try {
      setShow( !show );
    } catch (error) {
      console.warn(error);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const is_visible = ( status === 0 && review_flags.includes( -1 ) ) || ( status === 1 && review_flags.includes( 1 ) ) || ( status === 2 && review_flags.some( x => [1,2].includes( x ) ) ) || ( status === 4 && review_flags.some( x => [-1,2].includes( x ) ) );

    if( !is_visible ) return null;

    const slot_props = {
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [12, -34],
              },
            },
          ],
        },
      }

    let column_count = 1;
    const blocks = data.blocks?.map( (block,row) => {
      // const block = data.blocks.items[block_id];
      const filtered = block.fields;
      column_count = filtered.length;

      const lengths = filtered.map( field => ( field.DisplayInfo ? Math.max(field.DisplayInfo.Length * 12, 20 ) : '1000' ) )
      const max = Math.max( ...lengths,  );
      
      return (
        <Box key={block + row} sx={ { display: 'flex', flexDirection: 'column', gridTemplateColumns: `repeat( ${filtered.length}, minmax(200px, ${max+'px'}) )`, width: '100%' } }  name='da_exeptions_field_input'>
          <div style={{display: 'flex'}}>
            { filtered.map( (field) => {
              const header = field.DisplayInfo ? field.DisplayInfo.DisplayHeader : '';
              return (
                <Tooltip key={field.Name} title={<h2 style={{margin: 0}}>{field.Name}</h2>} slotProps={slot_props} placement='bottom-start'><div style={{ fontSize: '20px', wordBreak: 'break-word', width: 'fit-content', minWidth : '300px', paddingLeft  : '16px',  }}>{field.document.NumCode ? field.document.NumCode + ' - ' + header : header}</div></Tooltip>
              )
            } ) }
          </div>
          <div style={{display: 'flex'}}>
            {filtered.map( (field, column) => <div key={field.Name} style={{ width: 'fit-content', minWidth : '300px', height: 'fit-content'}}><Field field={field} file_id={file_id} rule_id={data.rule_id} numcode={document_current} /></div> ) }
          </div>
        </Box>
      )
    });

    return (
      <Box { ...CONTAINER }>
        <Card { ...CARD }>
          <div style={{ display: 'flex', alignItems: 'center' }} >
            <CardHeader { ...HEADER } />
            <Spacer push_right />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'}}>
              { _status_icons[status] || <div /> }
              <Button sx={{ height: '20px'}} onClick={onClick_Open}>
                {expand_icon}
              </Button>
            </Box>
          </div>
          { show ? 
            <>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }} >
                <Box sx={{display: (data.is_table ? 'flex' : 'flex'), flexDirection: 'column',  width:'100%', overflow:'auto',
                  scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.primary[theme.palette.mode]}`,
                }}>
                  {blocks}
                </Box>
                <Spacer size='6px' />
              </div>
              <Box { ...FOOTER }>
                <CommentModal id={data.id} is_open={is_open2} value={comment} onClose={onModalClose} onConfirm={onOverrideModalConfirm} />
                <CommentModal id={data.id} is_open={is_open } value={comment} onClose={onModalClose} onConfirm={onCommentModalConfirm} />
                <Tooltip title={tooltip_overrule} >
                  <Button { ...BUTTON_OVERRULE } />
                </Tooltip>
                <IconButton {...COMMENT}>
                  <InsertComment />
                </IconButton>
              </Box>
            </> : null
          }
        </Card>
      </Box>
    );
  }

  //#endregion

  return display();
}
